import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';
import { useLanguage } from '../../context/language';

const StyledAboutSection = styled.section`
  max-width: 980px;

  .inner {
    display: grid;
    grid-template-columns: 3fr 2fr;
    grid-gap: 48px;

    @media (max-width: 768px) {
      display: block;
    }
  }
`;

const StyledText = styled.div`
  ul.skills-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(140px, 220px));
    grid-gap: 0 12px;
    padding: 0;
    margin: 24px 0 0;
    overflow: hidden;
    list-style: none;

    li {
      position: relative;
      margin-bottom: 10px;
      padding-left: 20px;
      font-family: var(--font-mono);
      font-size: var(--fz-xs);

      &:before {
        content: '▹';
        position: absolute;
        left: 0;
        color: var(--green);
        font-size: var(--fz-sm);
        line-height: 12px;
      }
    }
  }

  .value-title {
    margin-top: 28px;
    margin-bottom: 10px;
    font-size: var(--fz-xl);
  }

  ul.value-list {
    ${({ theme }) => theme.mixins.fancyList};
  }
`;

const StyledSnapshot = styled.aside`
  ${({ theme }) => theme.mixins.boxShadow};
  border-radius: var(--border-radius);
  background: var(--surface);
  border: 1px solid var(--surface-border);
  padding: 26px 24px;
  height: max-content;

  @media (max-width: 768px) {
    margin: 42px auto 0;
    max-width: 420px;
  }

  h3 {
    margin-bottom: 20px;
    font-size: var(--fz-xxl);
  }

  .stat {
    padding: 10px 0;
    border-bottom: 1px solid rgba(227, 237, 249, 0.08);

    &:last-of-type {
      border-bottom: 0;
      padding-bottom: 0;
    }

    .label {
      display: block;
      color: var(--light-slate);
      font-family: var(--font-mono);
      font-size: var(--fz-xxs);
      text-transform: uppercase;
      letter-spacing: 0.06em;
      margin-bottom: 5px;
    }

    .value {
      color: var(--lightest-slate);
      font-size: var(--fz-md);
      font-weight: 600;
    }
  }

  .chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 22px;
  }

  .chip {
    background: var(--green-tint);
    border: 1px solid rgba(66, 214, 196, 0.42);
    color: var(--lightest-slate);
    border-radius: 999px;
    padding: 6px 11px;
    font-family: var(--font-mono);
    font-size: var(--fz-xxs);
  }
`;

const About = () => {
  const { content } = useLanguage();
  const about = content.about;
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, [prefersReducedMotion]);

  return (
    <StyledAboutSection id="about" ref={revealContainer}>
      <h2 className="numbered-heading">{about.heading}</h2>

      <div className="inner">
        <StyledText>
          <div>
            {about.paragraphs.map((paragraph, i) => (
              <p key={i}>{paragraph}</p>
            ))}

            <h3 className="value-title">{about.valueTitle}</h3>

            <ul className="value-list">
              {about.valueList.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>

            <p>{about.techIntro}</p>
          </div>

          <ul className="skills-list">
            {about.skills.map((skill, i) => (
              <li key={i}>{skill}</li>
            ))}
          </ul>
        </StyledText>

        <StyledSnapshot>
          <h3>{about.snapshotTitle}</h3>

          {about.snapshotStats.map((stat, i) => (
            <div className="stat" key={i}>
              <span className="label">{stat.label}</span>
              <span className="value">{stat.value}</span>
            </div>
          ))}

          <div className="chips">
            {about.domains.map((domain, i) => (
              <span className="chip" key={i}>
                {domain}
              </span>
            ))}
          </div>
        </StyledSnapshot>
      </div>
    </StyledAboutSection>
  );
};

export default About;
